// InsightTable — table variant of an Insight tile.
// Renders rows from /api/insights/{key} using ``display.columns`` as the
// schema. Zero business logic — column labels, keys and formats all
// come from the backend registry.
import React from 'react';
import * as Icons from 'lucide-react';
import { formatInsightValue } from './format';

export function InsightTable({ insight, loading, error, isAr }) {
  const { title, description, display } = insight || {};
  const columns = display?.columns || [];
  const rows = insight?.rows || [];
  const Icon = (display?.icon && Icons[display.icon]) || Icons.Sparkles;

  return (
    <div
      data-testid={`insight-table-${insight?.key || 'unknown'}`}
      className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 transition-all overflow-hidden"
    >
      <div className="flex items-start justify-between px-5 pt-5 pb-3 border-b border-slate-100">
        <div className="space-y-1">
          <div className="text-sm font-semibold text-slate-700">{title}</div>
          {description && (
            <div className="text-xs text-slate-400 leading-relaxed">{description}</div>
          )}
        </div>
        {Icon && (
          <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 text-indigo-600">
            <Icon size={18} />
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        {error ? (
          <div className="p-5 text-sm text-rose-600" data-testid="insight-error">
            {isAr ? 'تعذّر تحميل البيانات' : 'Unable to load data'}
          </div>
        ) : loading ? (
          <div className="p-5 space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-5 rounded bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div
            className="p-6 text-center text-sm text-slate-400"
            data-testid="insight-empty"
          >
            {isAr ? 'لا توجد بيانات للعرض في النطاق المحدد' : 'No data available for the selected range'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50/70">
              <tr className={isAr ? 'text-slate-500 text-right' : 'text-slate-500 text-left'}>
                {columns.map((col) => {
                  const colLabel = isAr ? col.label : (col.label_en || col.label);
                  return (
                    <th
                      key={col.key}
                      className="px-4 py-2.5 font-semibold text-xs uppercase tracking-wider"
                    >
                      {colLabel}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr
                  key={idx}
                  className="border-t border-slate-100 hover:bg-indigo-50/40 transition-colors"
                >
                  {columns.map((col) => (
                    <td key={col.key} className="px-4 py-2.5 text-slate-700">
                      {formatInsightValue(row[col.key], col.format)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default InsightTable;
